import React, { useState, useCallback, useEffect } from 'react';
import { kpiApi } from '../../services/api';
import { useDbNavigate } from '../../context/DatabaseContext';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Tag, LayoutGrid, Wrench } from 'lucide-react';
import DashboardLayout from '../../components/common/DashboardLayout';
import DashboardTile from '../../components/common/DashboardTile';
import { PieTooltip } from '../../components/common/ChartTooltips';

const EquipmentDashboardPage = () => {
  const navigate = useDbNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kpis, setKpis] = useState(null);

  const loadKpis = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await kpiApi.getEquipment();
      setKpis(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const handler = () => loadKpis();
    window.addEventListener('pmd-database-changed', handler);
    return () => window.removeEventListener('pmd-database-changed', handler);
  }, [loadKpis]);

  const editColors = {
    'ADD':     '#22c55e',
    'MOD':     '#3b82f6',
    'DEL':     '#ef4444',
    'FUT':     '#8b5cf6',
    'N/A':     '#94a3b8',
    'Unknown': '#e2e8f0'
  };

  const oatColors = {
    'Yes':     '#22c55e',
    'No':      '#ef4444',
    'Partial': '#f59e0b',
    'Impact':  '#f97316',
    'N/A':     '#94a3b8',
    'Unknown': '#e2e8f0'
  };

  const palette = [
    '#3b82f6','#22c55e','#f59e0b','#ef4444','#8b5cf6',
    '#06b6d4','#f97316','#10b981','#6366f1','#ec4899',
    '#14b8a6','#84cc16','#a855f7','#0ea5e9','#d946ef'
  ];

  const toChartDataWithColors = (grouped, colorMap) => {
    if (!grouped || grouped.length === 0) return [];
    return grouped.filter(g => g.count > 0).map(g => ({
      name: g.key,
      value: g.count,
      fill: colorMap[g.key] || '#e2e8f0'
    }));
  };

  const toChartDataPalette = (grouped) => {
    if (!grouped || grouped.length === 0) return [];
    return grouped.filter(g => g.count > 0).map((g, i) => ({
      name: g.key,
      value: g.count,
      fill: palette[i % palette.length]
    }));
  };

  const MiniDonutCustom = ({ data, label }) => {
    const total = data.reduce((sum, d) => sum + d.value, 0);
    return (
      <div className="ip-section">
        <h4>{label}</h4>
        {data.length > 0 ? (
          <ResponsiveContainer width={90} height={90}>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                cx="50%"
                cy="50%"
                innerRadius={24}
                outerRadius={40}
                paddingAngle={1}
                animationDuration={600}
              >
                {data.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} stroke="none" />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip />} />
              <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" className="recharts-donut-label-sm">
                {total.toLocaleString()}
              </text>
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="donut-chart empty" style={{ width: 80, height: 80 }}>
            <span className="donut-center" style={{ width: 56, height: 56 }}>0</span>
          </div>
        )}
        <div className="ip-legend">
          {data.map((g, i) => (
            <span key={i} className="legend-item">
              <span className="legend-dot" style={{ backgroundColor: g.fill }} />
              {g.name}: {g.value.toLocaleString()}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout
      id="equipment"
      title="Equipment Dashboard"
      icon={Wrench}
      onRefresh={loadKpis}
      loading={loading}
      error={error}
    >
      <DashboardTile
        title="Tags"
        icon={Tag}
        onClick={() => navigate('/tags')}
        colSpan={3}
      >
        <div className="ip-address-grid">
          <MiniDonutCustom data={toChartDataWithColors(kpis?.tagsByEdit, editColors)} label="By Edit Type" />
          <MiniDonutCustom data={toChartDataWithColors(kpis?.tagsByOatComplete, oatColors)} label="OAT Complete" />
          <div className="ip-total">
            <span className="ip-total-value">{(kpis?.tags || 0).toLocaleString()}</span>
            <span className="ip-total-label">Total Tags</span>
          </div>
        </div>
      </DashboardTile>

      <DashboardTile
        title="Panels"
        icon={LayoutGrid}
        onClick={() => navigate('/panels')}
        colSpan={3}
      >
        <div className="ip-address-grid">
          <MiniDonutCustom data={toChartDataWithColors(kpis?.panelsByEdit, editColors)} label="By Edit Type" />
          <MiniDonutCustom data={toChartDataWithColors(kpis?.panelsByOatComplete, oatColors)} label="OAT Complete" />
          <MiniDonutCustom data={toChartDataPalette(kpis?.panelsByPanelType)} label="Panel Type" />
          <div className="ip-total">
            <span className="ip-total-value">{(kpis?.panels || 0).toLocaleString()}</span>
            <span className="ip-total-label">Total Panels</span>
          </div>
        </div>
      </DashboardTile>
    </DashboardLayout>
  );
};

export default EquipmentDashboardPage;
